
import clsx from 'clsx';
import React from 'react';

import { translate } from '../../../base/i18n';
import { connect } from '../../../base/redux';
import { PollsPane } from '../../../polls/components/web/index';
import { setVisitorTabFocused } from '../../actions.any';
import { setPrivateChatDisabledAndBroadcast, toggleChat } from '../../actions.web';
import AbstractChat, { type Props, _mapStateToProps } from '../AbstractChat';

import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import DisplayNameForm from './DisplayNameForm';
import KeyboardAvoider from './KeyboardAvoider';
import MessageContainer from './MessageContainer';
import MessageRecipient from './MessageRecipient';

/**
 * React Component for holding the chat feature in a side panel that slides in
 * and out of view.
 */
class Chat extends AbstractChat<Props> {
    /**
   * Reference to the React Component for displaying chat messages. Used for
   * scrolling to the end of the chat messages.
   */
    _messageContainerRef: Object;

    state = {
        chatPanelNode: null
    };

    /**
   * Initializes a new {@code Chat} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props: Props) {
        super(props);

        this._messageContainerRef = React.createRef();

        // Bind event handlers so they are only bound once for every instance.
        this._onChatTabKeyDown = this._onChatTabKeyDown.bind(this);
        this._onChatInputResize = this._onChatInputResize.bind(this);
        this._onChatInputType = this._onChatInputType.bind(this);
        this._onEscClick = this._onEscClick.bind(this);
        this._onPollsTabKeyDown = this._onPollsTabKeyDown.bind(this);
        this._onTogglePrivateChat = this._onTogglePrivateChat.bind(this);
        this._onToggleChat = this._onToggleChat.bind(this);
        this._setChatPanelNode = this._setChatPanelNode.bind(this);
        this._onToggleMainSource = this._onToggleMainSource.bind(this);
        this._onToggleVisitorSource = this._onToggleVisitorSource.bind(this);
    }

    /**
   * Implements {@code Component#componentDidMount}.
   *
   * @inheritdoc
   */
    componentDidMount() {
        this._scrollMessageContainerToBottom(true);
    }

    /**
   * Implements {@code Component#componentDidUpdate}.
   *
   * @inheritdoc
   */
    componentDidUpdate(prevProps) {
        if (this.props._messages !== prevProps._messages) {
            const isScrolledToBottom = this._messageContainerRef.current
                ? this._messageContainerRef.current._isScrolledToBottom
                : true;

            if (isScrolledToBottom) {
                this._scrollMessageContainerToBottom(true);
            }
        } else if (this.props._isOpen && !prevProps._isOpen) {
            this._scrollMessageContainerToBottom(false);
        }
    }

    _setChatPanelNode: (?HTMLElement) => void;

    /**
     * Sets the chat panel DOM node.
     *
     * @param {HTMLElement} node - The DOM node.
     * @returns {void}
     */
    _setChatPanelNode(node) {
        if (node && node !== this.state.chatPanelNode) {
            this.setState({ chatPanelNode: node });
        }
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const {
            _isOpen,
            _isPollsEnabled,
            _isPollsTabFocused,
            _showNamePrompt
        } = this.props;

        const participantsPaneStyle = {
            padding: _isOpen ? '0 10px 0 0px' : '0px'
        };

        return (
            <div
                className = { 'participants_pane' }

                // id='sideToolbarContainer'
                onKeyDown = { this._onEscClick }
                style = { participantsPaneStyle }>
                <ChatHeader
                    _isPollsTabFocused = { _isPollsTabFocused }
                    className = 'chat-header'
                    id = 'chat-header'
                    interfaceConfig = { this.props._interfaceConfig }
                    onCancel = { this._onToggleChat } />
                {_showNamePrompt
                    ? <DisplayNameForm isPollsEnabled = { _isPollsEnabled } />
                    : this._renderChat()
                }
            </div>
        );
    }

    _onChatInputResize: () => void;

    /**
   * Callback invoked when {@code ChatInput} changes height. Preserves
   * displaying the latest message if it is scrolled to.
   *
   * @private
   * @returns {void}
   */
    _onChatInputResize() {
        this._messageContainerRef.current.maybeUpdateBottomScroll();
    }

    _onChatInputType: () => void;

    /**
     * Callback invoked when typing in {@code ChatInput}. Scrolls the message
     * container to the bottom if it is not already there.
     *
     * @private
     * @returns {void}
     */
    _onChatInputType() {
        const isScrolledToBottom = this._messageContainerRef.current
            ? this._messageContainerRef.current._isScrolledToBottom
            : true;

        if (!isScrolledToBottom) {
            this._scrollMessageContainerToBottom(true);
        }
    }

    _onChatTabKeyDown: (KeyboardEvent) => void;

    /**
   * Key press handler for the chat tab.
   *
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
    _onChatTabKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            this._onToggleChatTab();
        }
    }

    _onEscClick: (KeyboardEvent) => void;

    /**
   * Click handler for the chat sidenav.
   *
   * @param {KeyboardEvent} event - Esc key click to close the popup.
   * @returns {void}
   */
    _onEscClick(event) {
        if (event.key === 'Escape' && this.props._isOpen) {
            event.preventDefault();
            event.stopPropagation();
            this._onToggleChat();
        }
    }

    _onTogglePrivateChat: () => void;

    /**
     * Toggles private chat.
     *
     * @returns {void}
     */
    _onTogglePrivateChat() {
        const { _isPrivateChatDisabled, dispatch } = this.props;

        dispatch(setPrivateChatDisabledAndBroadcast(!_isPrivateChatDisabled));
    }

    _onPollsTabKeyDown: (KeyboardEvent) => void;

    /**
   * Key press handler for the polls tab.
   *
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
    _onPollsTabKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            this._onTogglePollsTab();
        }
    }

    /**
   * Returns a React Element for showing chat messages and a form to send new
   * chat messages.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderChat() {
        const {
            _iAmVisitor,
            _isVisitorTabFocused,
            _isPollsTabFocused,
            _mainMessages,
            _visitorMessages
        } = this.props;

        const pollsTabStyle = {
            height: '100%',
            border: '2px solid #C7D5E9',
            borderRadius: '0px 0px 20px 20px',
            backgroundColor: 'white'
        };

        if (_isPollsTabFocused) {
            const pollsSourceFilter = _iAmVisitor ? 'visitor' : undefined;

            return (
                <>
                    <div
                        aria-labelledby = 'polls-tab'
                        id = 'polls-panel'
                        role = 'tabpanel'
                        style = { pollsTabStyle }>
                        <PollsPane sourceFilter = { pollsSourceFilter } />
                    </div>
                    <KeyboardAvoider />
                </>
            );
        }

        const chatTabStyle = {
            backgroundColor: 'white',
            color: 'black',
            border: '2px solid #C7D5E9',
            borderRadius: '0px 0px 20px 20px',
            position: 'relative'
        };

        const showVisitorThread = _iAmVisitor || _isVisitorTabFocused;
        const activeMessages = showVisitorThread
            ? _visitorMessages
            : _mainMessages;
        const isVisitorObserverMode = !_iAmVisitor && _isVisitorTabFocused;

        return (
            <>
                <div
                    aria-labelledby = 'chat-tab'
                    className = { clsx(
                        'chat-panel',
                        'chat-panel-no-tabs'
                    ) }
                    id = 'chat-panel'
                    ref = { this._setChatPanelNode }
                    role = 'tabpanel'
                    style = { chatTabStyle }>
                    <MessageContainer

                        // Remount on thread switch so the render window and
                        // scroll state reset for the newly shown thread.
                        key = { showVisitorThread ? 'visitor-thread' : 'main-thread' }
                        messages = { activeMessages }
                        ref = { this._messageContainerRef } />
                    {!isVisitorObserverMode && <MessageRecipient />}
                    <ChatInput
                        chatPanelNode = { this.state.chatPanelNode }
                        onResize = { this._onChatInputResize }
                        onSend = { this._onSendMessage }
                        onType = { this._onChatInputType } />
                    {this._renderSourceToggle('chat')}
                    {/* <KeyboardAvoider /> */}
                </div>
            </>
        );
    }

    /**
   * Returns a React Element showing the Chat and Polls tab.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderTabs() {
        return null;
    }

    /**
     * Renders a compact segmented control that lets main-room users switch the
     * active panel between main-room content and visitor content. Hidden for
     * visitors, who only ever see their own content, and hidden entirely while
     * the room is not running visitor mode, when there is no second thread to
     * switch to.
     *
     * @param {'chat' | 'polls'} mode - Which panel the toggle belongs to.
     * @private
     * @returns {ReactElement|null}
     */
    _renderSourceToggle(mode) {
        const {
            _iAmVisitor,
            _isVisitorTabFocused,
            _mainMessages,
            _visitorMessages,
            _visitorsModeEnabled,
            t
        } = this.props;

        if (_iAmVisitor || !_visitorsModeEnabled) {
            return null;
        }

        const isVisitorMode = _isVisitorTabFocused;
        const mainPillClass = `chat-source-pill ${isVisitorMode ? '' : 'chat-source-pill-active'}`;
        const visitorPillClass = `chat-source-pill ${isVisitorMode ? 'chat-source-pill-active' : ''}`;

        // Unread counters are only meaningful for the thread that is not
        // currently displayed.
        const unreadVisitor = mode === 'chat' && !isVisitorMode
            ? _visitorMessages.filter(m => !m.hasRead).length
            : 0;
        const unreadMain = mode === 'chat' && isVisitorMode
            ? _mainMessages.filter(m => !m.hasRead).length
            : 0;

        return (
            <div
                aria-label = { mode === 'chat' ? t('chat.tabs.chat') : t('chat.tabs.polls') }
                className = 'chat-source-switch'
                role = 'tablist'>
                <button
                    aria-selected = { !isVisitorMode }
                    className = { mainPillClass }
                    onClick = { this._onToggleMainSource }
                    role = 'tab'
                    type = 'button'>
                    {t('chat.source.main')}
                    {unreadMain > 0 && (
                        <span className = 'chat-source-badge'>
                            {unreadMain > 99 ? '99+' : unreadMain}
                        </span>
                    )}
                </button>
                <button
                    aria-selected = { isVisitorMode }
                    className = { visitorPillClass }
                    onClick = { this._onToggleVisitorSource }
                    role = 'tab'
                    type = 'button'>
                    {t('chat.source.visitor')}
                    {unreadVisitor > 0 && (
                        <span className = 'chat-source-badge'>
                            {unreadVisitor > 99 ? '99+' : unreadVisitor}
                        </span>
                    )}
                </button>
            </div>
        );
    }

    /**
   * Scrolls the chat messages so the latest message is visible.
   *
   * @param {boolean} withAnimation - Whether or not to show a scrolling
   * animation.
   * @private
   * @returns {void}
   */
    _scrollMessageContainerToBottom(withAnimation) {
        if (this._messageContainerRef.current) {
            this._messageContainerRef.current.scrollToBottom(withAnimation);
        }
    }

    _onSendMessage: (string) => void;

    _onToggleChat: () => void;

    /**
   * Toggles the chat window.
   *
   * @returns {Function}
   */
    _onToggleChat() {
        this.props.dispatch(toggleChat());
    }

    _onToggleMainSource: () => void;

    /**
     * Shows main-room content in the active panel.
     *
     * @private
     * @returns {void}
     */
    _onToggleMainSource() {
        this.props.dispatch(setVisitorTabFocused(false));
    }

    _onToggleVisitorSource: () => void;

    /**
     * Shows visitor content in the active panel.
     *
     * @private
     * @returns {void}
     */
    _onToggleVisitorSource() {
        this.props.dispatch(setVisitorTabFocused(true));
    }

    _onTogglePollsTab: () => void;
    _onToggleChatTab: () => void;
}

/**
 * Maps (parts of) the redux state to {@link Chat} React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @private
 * @returns {{
 *     _isOpen: boolean,
 *     _messages: Array<Object>,
 *     _showNamePrompt: boolean
 * }}
 */
function mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}

export default translate(connect(_mapStateToProps, mapDispatchToProps)(Chat));
